import React, { useMemo } from 'react';

import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

const CARD_SX = { flex: 1, minWidth: 110, p: 1.5, border: 1, borderColor: 'grey.200', borderRadius: 2 } as const;

interface UploadResultRow {
  row: number;
  status: 'Failed' | 'Skipped' | 'Queued';
  id_salesforce_external: string;
  offerId: string;
  propertyId?: string;
  message: string;
}

interface UploadResult {
  total: number;
  failed: number;
  skipped: number;
  rows: Array<UploadResultRow>;
}

const STATUS_COLOR: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  Queued: 'success',
  Failed: 'error',
  Skipped: 'warning',
};

const columns: Array<GridColDef> = [
  { field: 'row', headerName: 'Row', width: 70 },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    renderCell: (params) => <Chip label={params.value} color={STATUS_COLOR[params.value] ?? 'default'} size="small" />,
  },
  { field: 'id_salesforce_external', headerName: 'Vendor Id', flex: 1, minWidth: 140 },
  { field: 'offerId', headerName: 'Offer Id', flex: 1, minWidth: 160 },
  { field: 'message', headerName: 'Message', flex: 1.5, minWidth: 200 },
];

interface Props {
  result: UploadResult;
}

export default function PropertiesUploadResult({ result }: Props) {
  const rows = useMemo(() => (result.rows ?? []).map((row) => ({ id: row.row, ...row })), [result.rows]);
  const queued = useMemo(() => (result.rows ?? []).filter((row) => row.status === 'Queued').length, [result.rows]);

  return (
    <Box>
      <Stack direction="row" spacing={2} mb={1.5} flexWrap="wrap">
        <Paper elevation={0} sx={CARD_SX}>
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            Total
          </Typography>
          <Typography variant="h5" fontWeight={600}>
            {result.total}
          </Typography>
        </Paper>
        <Paper elevation={0} sx={CARD_SX}>
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            Queued
          </Typography>
          <Typography variant="h5" fontWeight={600} color={queued > 0 ? 'success.main' : 'text.disabled'}>
            {queued}
          </Typography>
        </Paper>
        <Paper elevation={0} sx={CARD_SX}>
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            Failed
          </Typography>
          <Typography variant="h5" fontWeight={600} color={result.failed > 0 ? 'error.main' : 'text.disabled'}>
            {result.failed}
          </Typography>
        </Paper>
        <Paper elevation={0} sx={CARD_SX}>
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            Skipped
          </Typography>
          <Typography variant="h5" fontWeight={600} color={result.skipped > 0 ? 'warning.main' : 'text.disabled'}>
            {result.skipped}
          </Typography>
        </Paper>
      </Stack>
      <Paper elevation={0} sx={{ border: 1, borderColor: 'grey.200', borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableColumnMenu
          autoHeight
          sx={{ border: 0 }}
        />
      </Paper>
    </Box>
  );
}
